<script>
  import { onMount, onDestroy } from 'svelte';
  import { getApiUrl } from '../api';
  import { getCurrencySymbol } from '../utils';
  import { selectedCountry } from '../countryStore';
  import { t } from '../i18nStore';
  import UserTransactionsModal from '../components/UserTransactionsModal.svelte';

  let topUsers = [];
  let isLoading = true;
  let error = "";
  let currency = "$";
  
  let selectedUserId = null;
  let selectedUserName = '';
  let showModal = false;

  let period = 'today'; // Default period
  const periods = [
      { id: 'today', labelKey: 'top_savers.period.day' },
      { id: 'week', labelKey: 'top_savers.period.week' },
      { id: 'month', labelKey: 'top_savers.period.month' },
      { id: 'year', labelKey: 'top_savers.period.year' },
      { id: 'all_time', labelKey: 'top_savers.period.all_time' }
  ];

  let countryUnsubscribe;

  onMount(() => {
    countryUnsubscribe = selectedCountry.subscribe((code) => {
        currency = getCurrencySymbol(code);
        fetchLeaderboard(code, period);
    });
  });

  onDestroy(() => {
    if (countryUnsubscribe) countryUnsubscribe();
  });

  async function fetchLeaderboard(countryCode, selectedPeriod) {
    isLoading = true;
    error = "";
    try {
      const response = await fetch(getApiUrl(`/rewards/leaderboard?countryCode=${countryCode}&period=${selectedPeriod}`));
      if (!response.ok) {
        throw new Error("Failed to fetch leaderboard.");
      }
      const result = await response.json();
      
      // FIX: Robustly extract the array depending on how the backend formatted the JSON
      topUsers = Array.isArray(result) ? result : (result.leaderboard || result.data || []);
      
    } catch (e) {
      error = e.message;
    } finally {
      isLoading = false;
    }
  }

  function setPeriod(p) {
      period = p;
      fetchLeaderboard($selectedCountry, period);
  }

  function openTransactions(user) {
    selectedUserId = user.userId;
    selectedUserName = user.displayName;
    showModal = true;
  }
</script>

<UserTransactionsModal 
  bind:showModal={showModal} 
  userId={selectedUserId} 
  userName={selectedUserName} 
  onClose={() => showModal = false} 
/>

<div class="top-savers-container">
  <h2>{$t('top_savers.title')}</h2>
  <p class="subtitle">{$t('top_savers.subtitle')} <strong>{$selectedCountry}</strong></p>

  <!-- Period Selection Buttons -->
  <div class="period-selector">
    {#each periods as p}
      <button 
        class="period-btn {period === p.id ? 'active' : ''}" 
        on:click={() => setPeriod(p.id)}
      >
        {$t(p.labelKey) || p.id}
      </button>
    {/each}
  </div>

  {#if isLoading}
    <p class="loading">{$t('common.loading') || 'Loading...'}</p>
  {:else if error}
    <p class="error">{error}</p>
  {:else if topUsers && topUsers.length === 0}
    <p class="no-data">{$t('top_savers.no_data') || 'No data found.'}</p>
  {:else}
    <div class="table-responsive">
      <table>
        <thead>
          <tr>
            <th>{$t('top_savers.rank')}</th>
            <th>{$t('top_savers.user')}</th>
            <th>{$t('top_savers.savings')}</th>
            <th>{$t('top_savers.view_transactions')}</th>
          </tr>
        </thead>
        <tbody>
          {#each topUsers as user, index}
            <tr>
              <td class="rank-col">
                  {#if index === 0} 🥇
                  {:else if index === 1} 🥈
                  {:else if index === 2} 🥉
                  {:else} {index + 1} {/if}
              </td>
              <td class="user-col">{user.displayName}</td>
              <td class="savings-col">{currency}{(user.totalEarned || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
              <td class="action-col">
                 <button class="view-btn" on:click={() => openTransactions(user)}>
                   {$t('top_savers.view_transactions')}
                 </button>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</div>

<style>
  .top-savers-container {
    max-width: 800px;
    margin: 2rem auto;
    padding: 0 1rem;
    text-align: center;
  }
  h2 { color: #333; margin-bottom: 0.5rem; }
  .subtitle { color: #666; margin-bottom: 1.5rem; font-size: 1.1rem; }
  
  .period-selector {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 2rem;
  }
  
  .period-btn {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    color: #495057;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    cursor: pointer;
    font-size: 0.95rem;
    font-weight: 500;
    transition: all 0.2s ease;
  }
  
  .period-btn:hover {
    background-color: #e9ecef;
  }
  
  .period-btn.active {
    background-color: #007bff;
    color: white;
    border-color: #007bff;
    box-shadow: 0 2px 4px rgba(0, 123, 255, 0.2);
  }
  
  .table-responsive {
    overflow-x: auto;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0,0,0,0.05);
  }
  
  table { width: 100%; border-collapse: collapse; text-align: left; }
  th, td { padding: 1rem; border-bottom: 1px solid #eee; }
  th { background-color: #f8f9fa; font-weight: bold; color: #495057; }
  
  .rank-col { font-size: 1.2rem; text-align: center; width: 60px; }
  .user-col { font-weight: 500; color: #333; }
  .savings-col { font-weight: bold; color: #28a745; }
  .action-col { text-align: center; }

  .view-btn {
      background-color: #e9ecef; color: #495057; border: none; padding: 0.5rem 1rem;
      border-radius: 4px; cursor: pointer; font-size: 0.9rem; transition: background-color 0.2s;
  }
  .view-btn:hover { background-color: #dee2e6; color: #212529; }
  
  .loading, .error, .no-data { text-align: center; margin-top: 2rem; font-size: 1.1rem; }
  .error { color: red; }
  .no-data { color: #666; font-style: italic; }
</style>